import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAuditoriaCotacoes,
  fetchAuditoriaTimeline,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import {
  AuditoriaCotacaoItem,
  AuditoriaEvento,
  AuditoriaTimeline,
  COTACAO_STATUS_LABEL,
} from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import AuditoriaPropostaModal from './AuditoriaPropostaModal';
import AuditoriaConvitesModal from './AuditoriaConvitesModal';
import AuditoriaDecisoesModal from './AuditoriaDecisoesModal';
import AuditoriaEscolhasAtuais from './AuditoriaEscolhasAtuais';
import AuditoriaPedidosModal from './AuditoriaPedidosModal';
import SearchIcon from '../../../img/search-icon.svg';
import FilterIcon from '../../../img/filter.svg';
import CleanIcon from '../../../img/cross-circle.svg';
/*
 * Versão de traço LARANJA (`src/img/`), não a do menu: a do `components/sidebar/img/` tem
 * traço branco, porque a barra lateral é laranja — aqui o fundo do botão é branco e o
 * ícone sumiria.
 */
import CotacoesIcon from '../../../img/cotacoes.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import '../../../components/como-funciona/help-circle-btn.css';
/*
 * As duas folhas que este componente consome, importadas EXPLICITAMENTE mesmo já vindo pela
 * página que o hospeda (BUG-V74/at-67 — depender do CSS que outra tela carrega é
 * acoplamento invisível, e some no dia em que o componente for reusado):
 *  - `Recepcao.css`: a fileira de controles (`VisualizarOrdenar`, `CtaBig`, `ComponentName`)
 *    e a barra de contagem (`QuantidadeDeCarros`);
 *  - `AuditoriaPage.css`: o layout de duas colunas, os cards, a timeline e os modais.
 */
import '../../recepcao/pages/Recepcao.css';
import '../pages/AuditoriaPage.css';
import '../../../shared/ui/sub-abas.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Auditoria de COTAÇÕES (at-05) — a segunda metade da tela de Auditoria.
 *
 * A ESTRUTURA espelha a de veículos (14/08/2026, pedido do Victor: "preciso que se assemelhe
 * à auditoria de veículos — estruturas, filtros, posições"): mesma fileira de controles,
 * mesma barra de contagem, mesmo par "Resumo Atual" + "Linha do Tempo", mesmos modais. As
 * classes são as mesmas (`auditoria-*`), não cópias — duas metades da mesma tela que
 * divergissem no visual seriam lidas como telas diferentes.
 *
 * A humanização dos eventos vem PRONTA do backend do Portal: aqui não há mapa de rótulos de
 * ação, e é de propósito — quem conhece o significado de uma `action` é quem a grava.
 */

const ORIGEM_LABEL: Record<AuditoriaEvento['origem'], string> = {
  INTERNO: 'Equipe',
  FORNECEDOR: 'Fornecedor',
  SISTEMA: 'Sistema',
};

const COTACOES_POR_PAGINA = 10;

interface FiltrosCotacao {
  dataInicial: string;
  dataFinal: string;
  origem: string;
  autor: string;
}

const FILTROS_VAZIOS: FiltrosCotacao = { dataInicial: '', dataFinal: '', origem: '', autor: '' };

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

interface AuditoriaCotacoesProps {
  /**
   * Cotação a abrir já selecionada (14/08/2026): é como a auditoria do VEÍCULO chega até
   * aqui — o card do veículo lista as cotações da placa e o clique troca de aba trazendo
   * esta prop. Consumida uma única vez, no mount (`onCotacaoInicialConsumida`).
   */
  cotacaoInicial?: AuditoriaCotacaoItem | null;
  onCotacaoInicialConsumida?: () => void;
  /**
   * Ponte INVERSA (14/08/2026): a placa no resumo da cotação leva à auditoria do VEÍCULO.
   * A resolução placa → atendimento é do pai — é ele quem tem a seleção de veículo.
   */
  onAbrirVeiculo?: (placa: string) => void;
}

const AuditoriaCotacoes: React.FC<AuditoriaCotacoesProps> = ({
  cotacaoInicial = null,
  onCotacaoInicialConsumida,
  onAbrirVeiculo,
}) => {
  // ── Seleção da cotação ────────────────────────────────────────────────────
  const [selecionada, setSelecionada] = useState<AuditoriaCotacaoItem | null>(null);
  const [modalCotacoesAberto, setModalCotacoesAberto] = useState(false);
  const [buscaInput, setBuscaInput] = useState('');
  const [pagina, setPagina] = useState(1);
  const [opcoes, setOpcoes] = useState<AuditoriaCotacaoItem[]>([]);
  const [totalOpcoes, setTotalOpcoes] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);

  // ── Timeline ──────────────────────────────────────────────────────────────
  const [timeline, setTimeline] = useState<AuditoriaTimeline | null>(null);
  const [carregandoTimeline, setCarregandoTimeline] = useState(false);
  const [erroTimeline, setErroTimeline] = useState<string | null>(null);

  // ── Filtros, ajuda e modais de detalhe ───────────────────────────────────
  const [filtros, setFiltros] = useState<FiltrosCotacao>(FILTROS_VAZIOS);
  const [filtrosTemp, setFiltrosTemp] = useState<FiltrosCotacao>(FILTROS_VAZIOS);
  const [modalFiltrosAberto, setModalFiltrosAberto] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  /*
   * Guarda o EVENTO inteiro, não só o `abrir`: o modal de decisões precisa também de quando
   * e por quem, que são do evento. Montagem condicional (padrão de modais do projeto), então
   * o estado interno de cada modal nasce a cada abertura.
   */
  const [aberto, setAberto] = useState<AuditoriaEvento | null>(null);

  /*
   * Qual leitura está na tela: o HISTÓRICO ou o AGORA. Nasce em 'timeline' porque é a
   * pergunta que se faz primeiro ao abrir a auditoria; a outra responde o que vem depois
   * ("e como ficou?").
   */
  const [visao, setVisao] = useState<'timeline' | 'escolhas'>('timeline');

  /*
   * Erro de carga NUNCA vira estado vazio silencioso (regra do at-60). Aqui é pior que o
   * normal: numa tela de auditoria, lista vazia por falha é indistinguível de "não existe
   * registro", e quem investiga tira a conclusão oposta à correta.
   */
  const buscar = useCallback(async (termo: string, page: number) => {
    setBuscando(true);
    try {
      const resposta = await fetchAuditoriaCotacoes(termo, page, COTACOES_POR_PAGINA);
      setOpcoes(resposta.data);
      setTotalOpcoes(resposta.total);
      setErroBusca(null);
    } catch (error) {
      setOpcoes([]);
      setTotalOpcoes(0);
      setErroBusca('Não foi possível buscar as cotações. Tente novamente; se persistir, acione a TI.');
      notify.errorFrom(error, 'Não foi possível buscar as cotações.');
    } finally {
      setBuscando(false);
    }
  }, []);

  // Busca só enquanto o modal está aberto — fechado, não há o que manter atualizado.
  useEffect(() => {
    if (!modalCotacoesAberto) return;
    const id = setTimeout(() => buscar(buscaInput, pagina), 300);
    return () => clearTimeout(id);
  }, [modalCotacoesAberto, buscaInput, pagina, buscar]);

  const carregarTimeline = useCallback(async (cotacaoId: string) => {
    setCarregandoTimeline(true);
    try {
      const dados = await fetchAuditoriaTimeline(cotacaoId);
      setTimeline(dados);
      setErroTimeline(null);
    } catch (error) {
      // Timeline em branco leria como "esta cotação não teve movimento" — afirmação de
      // negócio tirada de uma falha de rede.
      setTimeline(null);
      setErroTimeline(
        'Não foi possível carregar a auditoria desta cotação. Tente novamente; se persistir, acione a TI.',
      );
      notify.errorFrom(error, 'Não foi possível carregar a auditoria desta cotação.');
    } finally {
      setCarregandoTimeline(false);
    }
  }, []);

  function escolher(cotacao: AuditoriaCotacaoItem) {
    setSelecionada(cotacao);
    setModalCotacoesAberto(false);
    setFiltros(FILTROS_VAZIOS);
    carregarTimeline(cotacao.id);
  }

  /*
   * Vindo da auditoria do VEÍCULO: abre já com a cotação selecionada. Roda uma vez, no
   * mount — o componente é montado a cada entrada na aba, e o consumo avisa o pai para não
   * reaplicar a mesma cotação numa visita futura.
   */
  useEffect(() => {
    if (cotacaoInicial) {
      escolher(cotacaoInicial);
      onCotacaoInicialConsumida?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function limpar() {
    setSelecionada(null);
    setTimeline(null);
    setErroTimeline(null);
    setFiltros(FILTROS_VAZIOS);
    // Volta à leitura inicial: "Escolhas atuais" sem cotação escolhida não tem o que mostrar.
    setVisao('timeline');
  }

  /*
   * Filtro no CLIENTE, e não no servidor: a timeline de uma cotação vem inteira numa
   * requisição (dezenas de eventos, não milhares) e é a fusão de três fontes — filtrar no
   * backend exigiria repetir o critério em cada uma delas, com risco de divergirem. Do lado
   * de veículos o volume justifica o servidor; aqui, não.
   */
  const eventosFiltrados = useMemo(() => {
    const todos = timeline?.eventos ?? [];
    return todos.filter((e) => {
      if (filtros.origem && e.origem !== filtros.origem) return false;
      if (filtros.autor && e.autor !== filtros.autor) return false;
      if (filtros.dataInicial && new Date(e.em) < new Date(`${filtros.dataInicial}T00:00:00`)) return false;
      if (filtros.dataFinal && new Date(e.em) > new Date(`${filtros.dataFinal}T23:59:59`)) return false;
      return true;
    });
  }, [timeline, filtros]);

  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;
  const totalPaginas = Math.max(1, Math.ceil(totalOpcoes / COTACOES_POR_PAGINA));
  const cotacao = timeline?.cotacao;

  const ajudaSecoes: ComoFuncionaSecao[] = [
    {
      titulo: 'Duas leituras da mesma cotação',
      conteudo: (
        <p>
          A <strong>Linha do Tempo</strong> responde <strong>o que mudou</strong>, quando e por quem — cada
          salvamento, cada aprovação, cada resposta de fornecedor tem o seu registro. As{' '}
          <strong>Escolhas atuais</strong> respondem <strong>como está agora</strong>: uma linha por peça, com
          a decisão que vale neste momento e quanto a cesta custa a mais que a soma dos menores preços. Numa
          cotação com muitas alterações, é ela que dá o quadro geral.
        </p>
      ),
    },
    {
      titulo: 'De onde vêm estes eventos',
      conteudo: (
        <p>
          Cada evento traz a sua origem: <strong>Equipe</strong> (o que comprador e gestor fizeram),{' '}
          <strong>Fornecedor</strong> (abriu o link, visualizou, enviou ou recusou proposta, ou tentou entrar
          com um link vencido) e <strong>Sistema</strong> (o envio de cada convite, com a falha quando houve).
          Os registros são gravados no momento de cada ação e <strong>não podem ser alterados</strong> por esta
          tela.
        </p>
      ),
    },
    {
      titulo: 'Eventos que abrem detalhes',
      conteudo: (
        <div>
          <p>
            Vários eventos têm o título <strong>clicável</strong>: o envio de convites abre a lista de
            fornecedores convidados; o envio de proposta abre a proposta como o fornecedor a enviou —{' '}
            <strong>na versão daquele momento</strong>, mesmo que ele tenha reenviado depois;{' '}
            <strong>Escolhas do comprador salvas</strong> abre quais peças foram escolhidas, de qual
            fornecedor, por qual valor e com qual justificativa; <strong>Cesta fechada</strong> abre o que
            havia na cesta naquele fechamento; e <strong>Pedidos gerados no ERP</strong> abre cada pedido,
            com os itens e valores que foram efetivamente enviados.
          </p>
          <p>
            As escolhas mostradas são as <strong>daquele salvamento</strong>: salvamentos posteriores podem
            tê-las alterado, e cada um tem o seu próprio registro na linha do tempo.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Nome de quem agiu',
      conteudo: (
        <p>
          Registros anteriores a agosto de 2026 podem mostrar um código no lugar do nome: o nome passou a ser
          guardado junto do registro só a partir dessa data, e não há como recuperá-lo para trás.
        </p>
      ),
    },
  ];

  return (
    <>
      {/* Fileira de controles — mesma estrutura e posições da auditoria de veículos. */}
      <div className="VisualizarOrdenar">
        <div
          className="Frame1000004037"
          style={{
            alignSelf: 'stretch',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 12,
            display: 'flex',
          }}
        >
          <div className="CtaBig MouseHover" onClick={() => { setBuscaInput(''); setPagina(1); setModalCotacoesAberto(true); }}>
            <div className="Frame1000003987">
              <img src={CotacoesIcon} alt="" width="20" height="20" />
              <div className="ComponentName">Cotações</div>
            </div>
          </div>

          <div
            className="CtaBig MouseHover"
            onClick={() => { setFiltrosTemp(filtros); setModalFiltrosAberto(true); }}
          >
            <div className="Frame1000003987">
              <img src={FilterIcon} alt="" width="19.8" height="19.8" />
              <div className="ComponentName">Filtros {filtrosAtivos > 0 ? `(${filtrosAtivos})` : ''}</div>
            </div>
          </div>

          <div className="CtaBig MouseHover" onClick={limpar}>
            <div className="Frame1000003987">
              <img src={CleanIcon} alt="" width="20" height="20" />
              <div className="ComponentName">Limpar</div>
            </div>
          </div>
        </div>
      </div>

      <div className="QuantidadeDeCarros auditoria-summary-bar">
        <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
          <div className="CtaQuantidadeDeCarros">
            <div className="Frame1000003987">
              <div className="ComponentName">
                {selecionada
                  ? eventosFiltrados.length < (timeline?.eventos.length ?? 0)
                    ? `${eventosFiltrados.length} de ${timeline?.eventos.length} eventos exibidos para ${selecionada.numero}`
                    : `${eventosFiltrados.length} eventos encontrados para ${selecionada.numero}`
                  : 'Nenhuma cotação selecionada'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="auditoria-layout">
        <div className="auditoria-summary-card">
          <div className="auditoria-card-heading">Resumo Atual</div>

          {!selecionada && (
            <div className="auditoria-empty-state">
              Nenhuma cotação selecionada. Por favor, selecione uma cotação para visualizar a auditoria.
            </div>
          )}

          {selecionada && cotacao && (
            <>
              <div className="auditoria-hero-row">
                <div>
                  <div className="auditoria-hero-title">{cotacao.numero}</div>
                  <div className="auditoria-hero-subtitle">{cotacao.veiculo || '—'}</div>
                </div>
                <span className="auditoria-status-badge" style={{ background: 'var(--tema-fundo, #fff4ee)', color: 'var(--tema-texto-laranja, #b4541a)' }}>
                  {COTACAO_STATUS_LABEL[cotacao.status] ?? cotacao.status}
                </span>
              </div>
              <div className="auditoria-summary-grid">
                {/*
                  A placa é a ponte de volta para a auditoria do VEÍCULO (14/08/2026) —
                  espelho da seção "Cotações de peças" do outro lado. Só vira link quando o
                  pai fornece a navegação e há placa; sem isso, texto comum.
                */}
                <div>
                  <strong>Placa:</strong>{' '}
                  {cotacao.placa && onAbrirVeiculo ? (
                    <button
                      type="button"
                      className="auditoria-link-veiculo"
                      title="Abrir a auditoria deste veículo"
                      onClick={() => onAbrirVeiculo(cotacao.placa as string)}
                    >
                      {cotacao.placa}
                    </button>
                  ) : (
                    cotacao.placa || '—'
                  )}
                </div>
                <div><strong>Criada em:</strong> {dataCurta(cotacao.createdAt)}</div>
                <div><strong>Comprador:</strong> {cotacao.compradorNome || '—'}</div>
                <div><strong>Solicitação:</strong> {cotacao.solicitacaoNumero || '—'}</div>
                <div><strong>Fornecedores:</strong> {cotacao._count.convites}</div>
                <div><strong>Prazo de resposta:</strong> {dataCurta(cotacao.prazoRespostaAte)}</div>
              </div>
            </>
          )}
        </div>

        <div className="auditoria-timeline-card">
          {/*
            Duas leituras da mesma cotação (14/08/2026, pedido do Victor): a LINHA DO TEMPO
            responde "o que mudou, quando e por quem"; as ESCOLHAS ATUAIS respondem "e como
            está agora?". Com muitos salvamentos, a primeira deixa de responder a segunda.
          */}
          <div className="auditoria-card-heading" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="sub-abas sub-abas--no-cabecalho" role="tablist" aria-label="Como ver a cotação">
              <button
                type="button"
                role="tab"
                aria-selected={visao === 'timeline'}
                className={`sub-aba ${visao === 'timeline' ? 'active' : ''}`}
                onClick={() => setVisao('timeline')}
              >
                Linha do Tempo
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={visao === 'escolhas'}
                className={`sub-aba ${visao === 'escolhas' ? 'active' : ''}`}
                onClick={() => setVisao('escolhas')}
                disabled={!selecionada}
              >
                Escolhas atuais
              </button>
            </div>
            <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setAjudaAberta(true)}>
              ?
            </button>
          </div>

          {visao === 'escolhas' && selecionada && (
            <AuditoriaEscolhasAtuais cotacaoId={selecionada.id} />
          )}

          {visao === 'timeline' && erroTimeline && (
            <div className="auditoria-error-state" role="alert">
              {erroTimeline}
            </div>
          )}

          {visao === 'timeline' && !erroTimeline && !selecionada && (
            <div className="auditoria-empty-state">
              Por favor, selecione uma cotação para visualizar a linha do tempo de auditoria.
            </div>
          )}

          {visao === 'timeline' && carregandoTimeline && <div className="auditoria-empty-state">Carregando a linha do tempo...</div>}

          {visao === 'timeline' && !erroTimeline && !carregandoTimeline && selecionada && eventosFiltrados.length === 0 && (
            <div className="auditoria-empty-state">Nenhum evento encontrado para os filtros informados.</div>
          )}

          {visao === 'timeline' && !carregandoTimeline && eventosFiltrados.length > 0 && (
            <div className="auditoria-timeline-list">
              {eventosFiltrados.map((evento) => (
                <div key={evento.id} className="auditoria-timeline-item">
                  <div className="auditoria-timeline-rail">
                    <div className={`auditoria-timeline-icon auditoria-timeline-icon--${evento.origem.toLowerCase()}`}>
                      {evento.origem === 'FORNECEDOR' ? 'F' : evento.origem === 'SISTEMA' ? 'S' : 'E'}
                    </div>
                    <div className="auditoria-timeline-line" />
                  </div>

                  <div className="auditoria-event-card">
                    <div className="auditoria-event-topline">
                      <span className="auditoria-event-date">{dataHora(evento.em)}</span>
                      <span className="auditoria-event-type">{ORIGEM_LABEL[evento.origem]}</span>
                    </div>

                    {/*
                      Evento que ABRE algo vira link — mesmo padrão dos eventos de
                      Planejamento e Liberação do lado de veículos. Quem decide é o `abrir`
                      vindo do backend: a tela não reconhece `action` nenhuma. Os demais
                      continuam texto, porque link que não leva a lugar nenhum é pior.
                    */}
                    <div className="auditoria-event-title-row">
                      {evento.abrir ? (
                        <button
                          type="button"
                          className="auditoria-event-title-link-btn"
                          onClick={() => setAberto(evento)}
                        >
                          {evento.titulo}
                        </button>
                      ) : (
                        <strong>{evento.titulo}</strong>
                      )}
                    </div>

                    <div className="auditoria-event-description">
                      {evento.detalhes.length > 0 ? evento.detalhes.join(' · ') : 'Sem descrição complementar.'}
                    </div>

                    <div className="auditoria-event-meta-grid">
                      {evento.abrir && (
                        <div>
                          <strong>Detalhe:</strong>{' '}
                          {evento.abrir.tipo === 'CONVITES'
                            ? 'Clique no título para ver os fornecedores convidados.'
                            : evento.abrir.tipo === 'DECISOES'
                              ? 'Clique no título para ver quais escolhas foram salvas.'
                              : evento.abrir.tipo === 'CESTA'
                                ? 'Clique no título para ver o que havia na cesta.'
                                : evento.abrir.tipo === 'PEDIDOS'
                                  ? 'Clique no título para ver os pedidos e seus itens.'
                              : 'Clique no título para ver a proposta enviada.'}
                        </div>
                      )}
                      {evento.fornecedor && (
                        <div><strong>Fornecedor:</strong> {evento.fornecedor}</div>
                      )}
                      <div><strong>Registrado por:</strong> {evento.autor || 'Fornecedor / Sistema'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal: selecionar cotação ─────────────────────────────────────── */}
      {modalCotacoesAberto && (
        <div className="auditoria-modal-overlay" role="presentation" onClick={() => setModalCotacoesAberto(false)}>
          <div className="auditoria-modal" role="dialog" aria-modal="true" aria-label="Selecionar cotação" onClick={(e) => e.stopPropagation()}>
            <div className="auditoria-modal-header">
              <h3>Selecionar cotação</h3>
              <button className="close-button" onClick={() => setModalCotacoesAberto(false)}>&times;</button>
            </div>

            <div className="auditoria-modal-body">
              <div className="auditoria-modal-search-wrapper">
                <img className="search-icon-inside" src={SearchIcon} alt="" width="18" height="18" />
                <input
                  className="auditoria-modal-search"
                  type="text"
                  placeholder="Buscar por número, placa, veículo ou solicitação..."
                  value={buscaInput}
                  onChange={(e) => { setBuscaInput(e.target.value); setPagina(1); }}
                  aria-label="Buscar cotação"
                  autoFocus
                />
              </div>

              {erroBusca ? (
                <div className="auditoria-error-state" role="alert">{erroBusca}</div>
              ) : (
                <div className="auditoria-modal-count">{totalOpcoes} cotação(ões) encontrada(s)</div>
              )}

              {buscando ? (
                <div className="auditoria-modal-loading">
                  <div className="spinner"></div>
                  <p>Buscando cotações...</p>
                </div>
              ) : (
                /*
                 * Classes do modal de VEÍCULOS (`auditoria-modal-option`, `-avatar`, `-info`,
                 * `-name`, `-username`, `is-selected`) — são as que têm regra em CSS, com
                 * hover e estado selecionado. A primeira versão desta tela inventou nomes
                 * (`-item`, `-item-main`) que não existiam em folha nenhuma: a lista saía
                 * crua, sem hover e sem separação entre as linhas.
                 */
                <div className="auditoria-modal-list" role="listbox" aria-label="Lista de cotações">
                  {!erroBusca && opcoes.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--tema-texto-3, #6b7280)', fontFamily: 'AmpleSoft-Regular, sans-serif' }}>
                      Nenhuma cotação encontrada para o filtro informado.
                    </div>
                  )}
                  {opcoes.map((c) => (
                    <div
                      key={c.id}
                      className={`auditoria-modal-option ${selecionada?.id === c.id ? 'is-selected' : ''}`}
                      role="option"
                      aria-selected={selecionada?.id === c.id}
                      onClick={() => escolher(c)}
                    >
                      <div className="auditoria-modal-avatar is-cotacao">
                        <img src={CotacoesIcon} alt="" style={{ width: '20px', height: '20px' }} />
                      </div>
                      <div className="auditoria-modal-info">
                        <span className="auditoria-modal-name">{c.numero}</span>
                        {/* `title`: o texto trunca com reticências, e o valor inteiro
                            continua alcançável no hover (regra de célula de 12/08/2026). */}
                        <span
                          className="auditoria-modal-username"
                          title={[c.placa, c.veiculo].filter(Boolean).join(' · ')}
                        >
                          {[c.placa, c.veiculo].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </div>
                      <span className="auditoria-modal-tag">
                        {COTACAO_STATUS_LABEL[c.status] ?? c.status}
                      </span>
                      {selecionada?.id === c.id && (
                        <div className="auditoria-modal-selected-indicator">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="auditoria-modal-pagination">
                <div
                  className={`auditoria-modal-page-btn ${pagina === 1 ? 'disabled' : ''}`}
                  onClick={() => pagina > 1 && setPagina(pagina - 1)}
                  style={{ cursor: pagina === 1 ? 'not-allowed' : 'pointer' }}
                >
                  <img className="imgIcon" src={pagina === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Seta para Esquerda" width="24" height="24" />
                  <div className="Anterior" style={{ color: pagina === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}>
                    Anterior
                  </div>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--tema-texto-2, #4b5563)', fontFamily: 'AmpleSoft-Bold, sans-serif', fontWeight: 700 }}>
                  Página {pagina} de {totalPaginas}
                </span>
                <div
                  className={`auditoria-modal-page-btn ${pagina >= totalPaginas ? 'disabled' : ''}`}
                  onClick={() => pagina < totalPaginas && setPagina(pagina + 1)}
                  style={{ cursor: pagina >= totalPaginas ? 'not-allowed' : 'pointer' }}
                >
                  <div className="PrXima" style={{ color: pagina >= totalPaginas ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}>
                    Próxima
                  </div>
                  <img className="imgIcon" src={pagina >= totalPaginas ? ArrowRightGray : ArrowRightOrange} alt="Seta para Direita" width="24" height="24" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: filtros (período, origem e autor) ─────────────────────── */}
      {modalFiltrosAberto && (
        <div className="auditoria-modal-overlay" role="presentation" onClick={() => setModalFiltrosAberto(false)}>
          <div className="auditoria-modal auditoria-filters-modal" role="dialog" aria-modal="true" aria-label="Filtros de Auditoria" onClick={(e) => e.stopPropagation()}>
            <div className="auditoria-modal-header">
              <h3>Filtrar Eventos</h3>
              <button type="button" className="close-button" onClick={() => setModalFiltrosAberto(false)}>
                &times;
              </button>
            </div>

            <div className="auditoria-filters-modal-body">
              <div className="auditoria-filter-field">
                <label htmlFor="cot-dataInicial">Data Inicial</label>
                <input
                  id="cot-dataInicial"
                  type="date"
                  value={filtrosTemp.dataInicial}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, dataInicial: e.target.value }))}
                />
              </div>

              <div className="auditoria-filter-field">
                <label htmlFor="cot-dataFinal">Data Final</label>
                <input
                  id="cot-dataFinal"
                  type="date"
                  value={filtrosTemp.dataFinal}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, dataFinal: e.target.value }))}
                />
              </div>

              <div className="auditoria-filter-field">
                <label htmlFor="cot-origem">Origem do Evento</label>
                <select
                  id="cot-origem"
                  value={filtrosTemp.origem}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, origem: e.target.value }))}
                >
                  <option value="">Todas as origens</option>
                  <option value="INTERNO">Equipe</option>
                  <option value="FORNECEDOR">Fornecedor</option>
                  <option value="SISTEMA">Sistema</option>
                </select>
              </div>

              {/*
                Autor (at-05, 14/08/2026, decisão do Victor): junto dos demais critérios, e
                não num botão próprio. A lista sai dos eventos da própria cotação — evento
                de FORNECEDOR não tem autor interno, então só quem é da equipe aparece aqui.
              */}
              <div className="auditoria-filter-field">
                <label htmlFor="cot-autor">Autor</label>
                <select
                  id="cot-autor"
                  value={filtrosTemp.autor}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, autor: e.target.value }))}
                  disabled={(timeline?.autores.length ?? 0) === 0}
                >
                  <option value="">
                    {(timeline?.autores.length ?? 0) === 0
                      ? 'Selecione uma cotação primeiro'
                      : 'Todos os autores'}
                  </option>
                  {(timeline?.autores ?? []).map((autor) => (
                    <option key={autor} value={autor}>{autor}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="auditoria-filters-modal-actions">
              <button type="button" className="auditoria-btn-clear" onClick={() => setFiltrosTemp(FILTROS_VAZIOS)}>
                Limpar
              </button>
              <button
                type="button"
                className="auditoria-btn-apply"
                onClick={() => { setFiltros(filtrosTemp); setModalFiltrosAberto(false); }}
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {aberto?.abrir?.tipo === 'PROPOSTA' && (
        <AuditoriaPropostaModal propostaId={aberto.abrir.propostaId} onFechar={() => setAberto(null)} />
      )}

      {aberto?.abrir?.tipo === 'CONVITES' && selecionada && (
        <AuditoriaConvitesModal
          cotacaoId={selecionada.id}
          quantidadeRegistrada={aberto.abrir.quantidadeRegistrada}
          onFechar={() => setAberto(null)}
        />
      )}

      {aberto?.abrir?.tipo === 'DECISOES' && (
        <AuditoriaDecisoesModal
          escolhas={aberto.abrir.escolhas}
          em={aberto.em}
          autor={aberto.autor}
          onFechar={() => setAberto(null)}
        />
      )}

      {/* Mesmo componente do modal de escolhas: os dois registram o mesmo formato. */}
      {aberto?.abrir?.tipo === 'CESTA' && (
        <AuditoriaDecisoesModal
          escolhas={aberto.abrir.cesta}
          total={aberto.abrir.total}
          resultado={aberto.abrir.resultado}
          em={aberto.em}
          autor={aberto.autor}
          titulo="Cesta Fechada"
          rotuloAutor="Fechada por"
          onFechar={() => setAberto(null)}
        />
      )}

      {aberto?.abrir?.tipo === 'PEDIDOS' && selecionada && (
        <AuditoriaPedidosModal cotacaoId={selecionada.id} onFechar={() => setAberto(null)} />
      )}

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Auditoria de Cotações"
        secoes={ajudaSecoes}
      />
    </>
  );
};

export default AuditoriaCotacoes;
